import {AnyImageOpResult, ValueOrNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeConvertColorSpace} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convert-color-space/node"
import {ImageOpNodeMeanAndVariance} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/mean-and-variance/node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpSystem} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"
import {readTexel} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/evaluate-and-read-texel"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"

// Converts sourceImage to Lab space (unless it's already grayscale, which is treated as an L-only
// signal). Shared between the recolor node's per-pixel Reinhard transform and computeLabStats below.
export function buildLabSource(
    imageOpNodeBag: ImageOpNodeBag,
    idPrefix: string,
    sourceImage: ValueOrNode<AnyImageOpResult>,
    sourceColorSpace: "linear" | "srgb",
    isGrayscale: boolean,
): ValueOrNode<AnyImageOpResult> {
    return isGrayscale
        ? sourceImage
        : imageOpNodeBag.getNode(`${idPrefix}ToLab`, ImageOpNodeConvertColorSpace, {
              sourceImage,
              sourceColorSpace,
              targetColorSpace: "lab",
          })
}

// Builds live mean/stddev graph nodes over an already-Lab-converted labSource, as opposed to
// eagerly evaluating them to JS constants (computeLabStats below). Used by
// ImageOpNodeOperatorRecolor as its fallback when the operator facade didn't eagerly supply
// sourceStats (see operator-recolor.ts) — e.g. during baking, where this rides along inside
// whatever evaluation (HAL warm-up or imgproc final) is already running, instead of adding a
// separate eager evaluate() pass.
export function buildLiveLabStats(imageOpNodeBag: ImageOpNodeBag, idPrefix: string, labSource: ValueOrNode<AnyImageOpResult>) {
    const statsNode = imageOpNodeBag.getNode(`${idPrefix}Stats`, ImageOpNodeMeanAndVariance, {sourceImage: labSource})
    const mean = imageOpNodeBag.getProperty<ImageOpNodeMeanAndVariance, AnyImageOpResult, "mean">(`${idPrefix}Mean`, statsNode, "mean")
    const variance = imageOpNodeBag.getProperty<ImageOpNodeMeanAndVariance, AnyImageOpResult, "variance">(`${idPrefix}Variance`, statsNode, "variance")
    const stddev = imageOpNodeBag.getNode(`${idPrefix}Stddev`, ImageOpNodeMath, {operator: "sqrt", operand: variance})
    return {mean, stddev}
}

// Computes the Lab-space per-channel mean/stddev of sourceImage in a throwaway ImageOpNodeBag that
// never touches the operator-stack pipeline. Shared by the recolor operator's own sourceStats
// (see compute-source-lab-stats.ts) and target-stats-from-reference-image (see
// recolor-reference-image.ts).
export async function computeLabStats(
    imageOpSystem: ImageOpSystem,
    sourceImage: ValueOrNode<AnyImageOpResult>,
    sourceColorSpace: "linear" | "srgb",
    isGrayscale: boolean,
): Promise<TextureEditNodes.OperatorRecolor["targetStats"]> {
    const bag = new ImageOpNodeBag("computeLabStats")
    bag.beginFrame()
    const labSource = buildLabSource(bag, "labStats", sourceImage, sourceColorSpace, isGrayscale)
    const {mean, stddev} = buildLiveLabStats(bag, "labStats", labSource)
    bag.endFrame()

    try {
        const meanValues = await readTexel(await imageOpSystem.evaluate("hal", mean))
        const stddevValues = await readTexel(await imageOpSystem.evaluate("hal", stddev))
        const DEFAULT_CHANNEL_STATS: TextureEditNodes.LabChannelStats = {mean: 0, stddev: 0.1}
        return {
            l: {mean: meanValues[0], stddev: stddevValues[0]},
            a: isGrayscale ? DEFAULT_CHANNEL_STATS : {mean: meanValues[1], stddev: stddevValues[1]},
            b: isGrayscale ? DEFAULT_CHANNEL_STATS : {mean: meanValues[2], stddev: stddevValues[2]},
        }
    } finally {
        bag.dispose()
    }
}
